import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { formatLocalDateYMD, parseDateOnlyToLocal } from '@shared/lib/formatLocalDate';
import { useCallback, useEffect, useState } from 'react';

/** Estado local de filtros para órdenes de facturación (eligible shipments) */
export interface InvoicesOrdersFiltersState {
  financialStatus: string[];
  type: string[];
  createdDateRange: { from: Date | undefined; to: Date | undefined };
  shippingDateRange: { from: Date | undefined; to: Date | undefined };
  dateTrackingUpdateDateRange: { from: Date | undefined; to: Date | undefined };
  lastStatusAtDateRange: { from: Date | undefined; to: Date | undefined };
  statusIds: string[];
  shipmentStatusIds: string[];
  teamIds: string[];
  shopIds: string[];
  period: string[];
  paymentMethodIds: string[];
  courierIds: string[];
  preInvoiceIds: string[];
  invoiceIds: string[];
}

const defaultState: InvoicesOrdersFiltersState = {
  financialStatus: [],
  type: [],
  createdDateRange: { from: undefined, to: undefined },
  shippingDateRange: { from: undefined, to: undefined },
  dateTrackingUpdateDateRange: { from: undefined, to: undefined },
  lastStatusAtDateRange: { from: undefined, to: undefined },
  statusIds: [],
  shipmentStatusIds: [],
  teamIds: [],
  shopIds: [],
  period: [],
  paymentMethodIds: [],
  courierIds: [],
  preInvoiceIds: [],
  invoiceIds: [],
};

/** Copia profunda de los anidados; si no, parseConditions muta defaultState y "Limpiar" no vacía las fechas. */
function cloneDefaultState(): InvoicesOrdersFiltersState {
  return {
    financialStatus: [...defaultState.financialStatus],
    type: [...defaultState.type],
    createdDateRange: { ...defaultState.createdDateRange },
    shippingDateRange: { ...defaultState.shippingDateRange },
    dateTrackingUpdateDateRange: { ...defaultState.dateTrackingUpdateDateRange },
    lastStatusAtDateRange: { ...defaultState.lastStatusAtDateRange },
    statusIds: [...defaultState.statusIds],
    shipmentStatusIds: [...defaultState.shipmentStatusIds],
    teamIds: [...defaultState.teamIds],
    shopIds: [...defaultState.shopIds],
    period: [...defaultState.period],
    paymentMethodIds: [...defaultState.paymentMethodIds],
    courierIds: [...defaultState.courierIds],
    preInvoiceIds: [...defaultState.preInvoiceIds],
    invoiceIds: [...defaultState.invoiceIds],
  };
}

function parseConditions(conditions: FilterConditionOutput[]): InvoicesOrdersFiltersState {
  const state = cloneDefaultState();

  for (const c of conditions) {
    if (c.column === 'financial_status' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.financialStatus = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'type' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.type = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'created_at') {
      if (c.action === 'greater_equal' && c.value) {
        state.createdDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.createdDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'shipping_date') {
      if (c.action === 'greater_equal' && c.value) {
        state.shippingDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.shippingDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'date_tracking_update') {
      if (c.action === 'greater_equal' && c.value) {
        state.dateTrackingUpdateDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.dateTrackingUpdateDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'last_status_at') {
      if (c.action === 'greater_equal' && c.value) {
        state.lastStatusAtDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.lastStatusAtDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (
      (c.column === 'status_id' || c.column === 'status_ids') &&
      (c.action === 'in' || c.action === 'not_in') &&
      c.value
    ) {
      state.statusIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (
      (c.column === 'shipment_status_id' || c.column === 'shipment_status_ids') &&
      (c.action === 'in' || c.action === 'not_in') &&
      c.value
    ) {
      state.shipmentStatusIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'team_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.teamIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'shop_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.shopIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'period' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.period = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'payment_method_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.paymentMethodIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'courier_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.courierIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'pre_invoice_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.preInvoiceIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    } else if (c.column === 'invoice_id' && (c.action === 'in' || c.action === 'not_in') && c.value) {
      state.invoiceIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    }
  }

  return state;
}

function convertToConditions(state: InvoicesOrdersFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.financialStatus.length > 0) {
    conditions.push({
      column: 'financial_status',
      action: 'in',
      value: state.financialStatus,
    });
  }
  if (state.type.length > 0) {
    conditions.push({
      column: 'type',
      action: 'in',
      value: state.type,
    });
  }
  if (state.createdDateRange.from) {
    conditions.push({
      column: 'created_at',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.createdDateRange.from),
    });
  }
  if (state.createdDateRange.to) {
    conditions.push({
      column: 'created_at',
      action: 'less_equal',
      value: formatLocalDateYMD(state.createdDateRange.to),
    });
  }
  if (state.shippingDateRange.from) {
    conditions.push({
      column: 'shipping_date',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.shippingDateRange.from),
    });
  }
  if (state.shippingDateRange.to) {
    conditions.push({
      column: 'shipping_date',
      action: 'less_equal',
      value: formatLocalDateYMD(state.shippingDateRange.to),
    });
  }
  if (state.dateTrackingUpdateDateRange.from) {
    conditions.push({
      column: 'date_tracking_update',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.dateTrackingUpdateDateRange.from),
    });
  }
  if (state.dateTrackingUpdateDateRange.to) {
    conditions.push({
      column: 'date_tracking_update',
      action: 'less_equal',
      value: formatLocalDateYMD(state.dateTrackingUpdateDateRange.to),
    });
  }
  if (state.lastStatusAtDateRange.from) {
    conditions.push({
      column: 'last_status_at',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.lastStatusAtDateRange.from),
    });
  }
  if (state.lastStatusAtDateRange.to) {
    conditions.push({
      column: 'last_status_at',
      action: 'less_equal',
      value: formatLocalDateYMD(state.lastStatusAtDateRange.to),
    });
  }
  if (state.statusIds.length > 0) {
    conditions.push({
      column: 'status_ids',
      action: 'in',
      value: state.statusIds,
    });
  }
  if (state.shipmentStatusIds.length > 0) {
    conditions.push({
      column: 'shipment_status_ids',
      action: 'in',
      value: state.shipmentStatusIds,
    });
  }
  if (state.teamIds.length > 0) {
    conditions.push({
      column: 'team_id',
      action: 'in',
      value: state.teamIds,
    });
  }
  if (state.shopIds.length > 0) {
    conditions.push({
      column: 'shop_id',
      action: 'in',
      value: state.shopIds,
    });
  }
  if (state.period.length > 0) {
    conditions.push({
      column: 'period',
      action: 'in',
      value: state.period,
    });
  }
  if (state.paymentMethodIds.length > 0) {
    conditions.push({
      column: 'payment_method_id',
      action: 'in',
      value: state.paymentMethodIds,
    });
  }
  if (state.courierIds.length > 0) {
    conditions.push({
      column: 'courier_id',
      action: 'in',
      value: state.courierIds,
    });
  }
  if (state.preInvoiceIds.length > 0) {
    conditions.push({
      column: 'pre_invoice_id',
      action: 'in',
      value: state.preInvoiceIds,
    });
  }
  if (state.invoiceIds.length > 0) {
    conditions.push({
      column: 'invoice_id',
      action: 'in',
      value: state.invoiceIds,
    });
  }

  return conditions;
}

export interface UseInvoicesOrdersFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

export const useInvoicesOrdersFilters = ({ initialConditions = [], onChange }: UseInvoicesOrdersFiltersParams) => {
  const [filters, setFilters] = useState<InvoicesOrdersFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: InvoicesOrdersFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleFinancialStatusChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, financialStatus: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleTypeChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, type: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleCreatedDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, createdDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleShippingDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, shippingDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleDateTrackingUpdateDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, dateTrackingUpdateDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleLastStatusAtDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, lastStatusAtDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleStatusIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, statusIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleShipmentStatusIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, shipmentStatusIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleTeamIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, teamIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleShopIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, shopIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePeriodChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, period: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePaymentMethodIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, paymentMethodIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleCourierIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, courierIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePreInvoiceIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, preInvoiceIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleInvoiceIdsChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, invoiceIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    const empty = cloneDefaultState();
    setFilters(empty);
    notifyChange(empty);
  }, [notifyChange]);

  return {
    filters,
    handleFinancialStatusChange,
    handleTypeChange,
    handleCreatedDateRangeChange,
    handleShippingDateRangeChange,
    handleDateTrackingUpdateDateRangeChange,
    handleLastStatusAtDateRangeChange,
    handleStatusIdsChange,
    handleShipmentStatusIdsChange,
    handleTeamIdsChange,
    handleShopIdsChange,
    handlePeriodChange,
    handlePaymentMethodIdsChange,
    handleCourierIdsChange,
    handlePreInvoiceIdsChange,
    handleInvoiceIdsChange,
    clearAllFilters,
  };
};
